import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import './footer.css';
import { useTranslation } from 'react-i18next';
import { LogosFooter } from '../../components/footer/LogosFooter';
import { CookiesMessage } from '../../components/footer/CookiesMessage';
import {
  FaBlogger,
  FaInstagram,
  FaLinkedin,
  FaPhoneAlt,
  FaTiktok,
  FaTwitter,
  FaYoutube,
} from 'react-icons/fa';
import { MdOutlineMailOutline } from 'react-icons/md';
import { TiSocialFacebookCircular } from 'react-icons/ti';
import { useColors } from 'frontend/hooks/useColors';

export default function Footer() {
  const { t } = useTranslation(['layout']);
  const applicationState = useSelector((state) => state.applicationState);
  
  const footer = useMemo(() => applicationState.templateData.footer, [applicationState.templateData.footer]);
  const principalOffice = useMemo(() => applicationState.templateData.principalOffice, [applicationState.templateData.principalOffice]);
  const header = useMemo(() => applicationState.templateData, [applicationState.templateData]);
  
  const templateConfig = useMemo(() => {
    const templateId = applicationState.templateId;
    const disabledLinks = applicationState.templateData.disabledLinks.map(
      (link) => link.defaultEnlace.id,
    );
    switch (templateId) {
      case 1: //Arg
        return {
          id: 1,
          showConsumerRights: true,
          showPaymentMethods: true && !disabledLinks.includes(2),
          showGeneralContractingConditions: true && !disabledLinks.includes(3),
        };
      case 2: //Brasil
        return {
          id: 2,
          showPrivacyPolicy: true && !disabledLinks.includes(1),
          showTermsOfUse: true && !disabledLinks.includes(4),
        };
      case 3: //Peru
        return {
          id: 3,
          showTermsOfUse: true && !disabledLinks.includes(5),
        };
      default:
        return {
          id: templateId,
          showConsumerRights: true,
          showPaymentMethods: true,
          showGeneralContractingConditions: true,
        };
    }
  }, [applicationState.templateId, applicationState.templateData.disabledLinks]);

  const { background, text } = useColors();

  const renderLabelWithText = (list) =>
    list
      .filter((item) => item.text && item.text !== '')
      .map((item) =>
        item.link ? (
          <a
            key={`${item.label}-${item.text}`}
            className="contact-line hover"
            href={`${item.preffix}${item.text}`}
          >
            {item.icon && <i className={`${item.icon} pr-2`} />}
            <span className="no-underline hover:underline">{item.text}</span>
          </a>
        ) : (
          <div key={`${item.label}-${item.text}`} className="contact-line">
            {item.icon && <i className={`${item.icon} pr-2`} />}
            {item.text}
          </div>
        ),
      );

  let infoList = [];
  if (principalOffice !== undefined) {
    infoList = [
      {
        text: principalOffice.direccion,
        icon: 'fas fa-map-marker-alt',
      },
      {
        text: principalOffice.horario,
        icon: 'fas fa-clock',
      },
    ];
  }

  const getGeneralConditionsText = (templateId) => {
    switch (templateId) {
      case 1:
        return t('footer.generalConditions');
      case 2:
        return 'Política de Privacidade';
      case 3:
        return 'Política de Privacidad';
      default:
        return t('footer.generalConditions');
    }
  };

  const getUseTermsText = (templateId) => {
    switch (templateId) {
      case 1:
        return t('footer.useTerms');
      case 2:
        return 'Termos de Uso Site respetivamente';
      case 3:
        return 'Términos y condiciones de Uso';
      default:
        return t('footer.useTerms');
    }
  };

  const contactInfo = useMemo(() => {
    return renderLabelWithText(infoList);
  }, [infoList]);

  return (
    <footer className="footer-content">
      <div className="py-8" style={{ backgroundColor: background, color: text }}>
        <div className="container">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2 py-8 justify-items-stretch lg:divide-x-2 lg:divide-solid">
            <div className="flex flex-col items-center md:items-start">
              <div className="w-3/4">
                <h3 className="text-2xl font-medium mb-8">{t('footer.about_us')}</h3>
                <div className="text-sm text-justify mb-8">
                  {footer.detalle &&
                    footer.detalle.map((detalle) =>
                      detalle.tipoEnlace?.nombre === 'about-us-footer' ? detalle.valor : '',
                    )}
                  <div className="lg:hidden mt-4">
                    {header.header.telefonoContacto && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          marginBottom: '4px',
                        }}
                      >
                        <FaPhoneAlt />
                        <span className="contact__icon">{header.header.telefonoContacto}</span>
                      </div>
                    )}
                    {header.header.emailContacto && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <MdOutlineMailOutline style={{ fontSize: '20px' }} />
                        <span
                          className="contact__icon"
                          style={{
                            textTransform: 'lowercase',
                            textTransform: 'lowercase',
                          }}
                        >
                          {header.header.emailContacto}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div
              className="contact-section"
              style={{
                borderLeftColor: text,
                borderRightColor: text,
              }}
            >
              <div className="flex flex-col items-center">
                <div className="w-3/4">
                  <div className="text-2xl font-medium mb-8">{t('footer.whereWeAre')}</div>
                  <div className="contact mb-8">
                    <div className="flex flex-col items-start">
                      <span>{footer.razonSocial.toUpperCase()}</span>
                      {templateConfig.id === 1 ? (
                        <span>Legajo {footer.legajo}</span>
                      ) : templateConfig.id === 2 ? (
                        <span> CADASTUR {footer.legajo}</span>
                      ) : null}
                      {templateConfig.id === 1 ? (
                        <span>CUIT {footer.cuit}</span>
                      ) : templateConfig.id === 2 ? (
                        <span>CNPJ {footer.cuit}</span>
                      ) : templateConfig.id === 3 ? (
                        <span>RUC {footer.cuit}</span>
                      ) : null}
                      <div className="flex flex-col items-start">
                        {contactInfo}
                        <a className="contact-line no-underline hover:underline" href="/agencies">
                          {t('footer.agencies')}
                        </a>
                      </div>
                    </div>

                    <div className="lg:hidden flex mt-4 space-x-4">
                      {header.socialNetworks.map((socialNetwork, index) => {
                        if (socialNetwork.tipoEnlace.descripcion === 'Instagram') {
                          return (
                            <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
                              <a
                                key={index}
                                href={socialNetwork.valor}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ alignItems: 'center' }}
                              >
                                <FaInstagram style={{ fontSize: '20px' }} />
                              </a>
                            </div>
                          );
                        }
                        return null;
                      })}
                      {header.socialNetworks.map((socialNetwork, index) => {
                        if (socialNetwork.tipoEnlace.descripcion === 'Facebook') {
                          return (
                            <div style={{ display: 'flex', alignItems: 'center' }} key={index}>
                              <a
                                href={socialNetwork.valor}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ alignItems: 'center' }}
                              >
                                <TiSocialFacebookCircular style={{ fontSize: '20px' }} />
                              </a>
                            </div>
                          );
                        }
                        return null;
                      })}
                      {header.socialNetworks.map((socialNetwork, index) => {
                        if (socialNetwork.tipoEnlace.descripcion === 'Linkedin') {
                          return (
                            <div style={{ display: 'flex', alignItems: 'center' }} key={index}>
                              <a
                                href={socialNetwork.valor}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ alignItems: 'center' }}
                              >
                                <FaLinkedin />
                              </a>
                            </div>
                          );
                        }
                        return null;
                      })}
                      {header.socialNetworks.map((socialNetwork, index) => {
                        if (socialNetwork.tipoEnlace.descripcion === 'YouTube') {
                          return (
                            <div style={{ display: 'flex', alignItems: 'center' }} key={index}>
                              <a
                                href={socialNetwork.valor}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ alignItems: 'center' }}
                              >
                                <FaYoutube style={{ fontSize: '20px' }} />
                              </a>
                            </div>
                          );
                        }
                        return null;
                      })}
                      {header.socialNetworks.map((socialNetwork, index) => {
                        if (socialNetwork.tipoEnlace.descripcion === 'Twitter') {
                          return (
                            <div style={{ display: 'flex', alignItems: 'center' }} key={index}>
                              <a
                                href={socialNetwork.valor}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ alignItems: 'center' }}
                              >
                                <FaTwitter style={{ fontSize: '20px' }} />
                              </a>
                            </div>
                          );
                        }
                        return null;
                      })}
                      {header.socialNetworks.map((socialNetwork, index) => {
                        if (socialNetwork.tipoEnlace.descripcion === 'Blog') {
                          return (
                            <div style={{ display: 'flex', alignItems: 'center' }} key={index}>
                              <a
                                href={socialNetwork.valor}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ alignItems: 'center' }}
                              >
                                <FaBlogger style={{ fontSize: '20px' }} />
                              </a>
                            </div>
                          );
                        }
                        return null;
                      })}
                      {header.socialNetworks.map((socialNetwork, index) => {
                        if (socialNetwork.tipoEnlace.descripcion === 'TikTok') {
                          return (
                            <div style={{ display: 'flex', alignItems: 'center' }} key={index}>
                              <a
                                href={socialNetwork.valor}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ alignItems: 'center' }}
                              >
                                <FaTiktok style={{ fontSize: '20px' }} />
                              </a>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="info-section"
              style={{
                borderLeftColor: text,
                borderRightColor: text,
              }}
            >
              <div className="flex flex-col items-center">
                <div className="w-3/4">
                  <div className="text-2xl font-medium mb-8">{t('footer.info')}</div>
                  <div className="content flex flex-col gap-1 items-start">
                    {templateConfig.showClaimsBook && (
                      <a className="no-underline hover:underline" href="/claims">
                        {t('footer.claimsBook')}
                      </a>
                    )}
                    {templateConfig.showPaymentMethods && (
                      <a
                        className="no-underline hover:underline"
                        href="/pay-methods"
                        target="_blank"
                      >
                        {t('footer.pay_methods')}
                      </a>
                    )}
                    {(templateConfig.showGeneralContractingConditions ||
                      templateConfig.showPrivacyPolicy) && (
                      <a
                        className="no-underline hover:underline"
                        href="/general-conditions"
                        target="_blank"
                      >
                        {getGeneralConditionsText(templateConfig.id)}
                      </a>
                    )}
                    {templateConfig.showTermsOfUse && (
                      <a
                        className="no-underline hover:underline"
                        href="/terms-and-conditions"
                        target="_blank"
                      >
                        {getUseTermsText(templateConfig.id)}
                      </a>
                    )}
                    {footer.utilLink &&
                      footer.utilLink.map((item) => (
                        <a
                          className="no-underline hover:underline"
                          href={item.valor}
                          target="_blank"
                          rel="noreferrer"
                          key={item.id}
                        >
                          {item.tipoEnlace?.descripcion}
                        </a>
                      ))}
                    {footer.legalLinks &&
                      footer.legalLinks.map((item) => (
                        <a
                          className="no-underline hover:underline"
                          href={item.valor}
                          target="_blank"
                          rel="noreferrer"
                          key={item.id}
                        >
                          {item.tipoEnlace?.descripcion}
                        </a>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {templateConfig.showConsumerRights && (
            <div className="terms-and-conditions-section text-sm text-justify">
              {t('footer.consumerRights')} - {t('footer.legalText')}
            </div>
          )}
          <div className="my-6 flex md:justify-between flex-col xl:flex-row">
            {templateConfig.showConsumerRights && (
              <div className="flex justify-between flex-col lg:flex-row">
                <div className="flex flex-col">
                  <a
                    className="block w-72 h-12 p-4 mb-4 bg-white text-sm text-black uppercase text-center rounded-md"
                    href={t('footer.link-reclamations')}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('footer.reclamations')}
                  </a>
                  <span>Ley 24.240</span>
                  <span>Autoridad Nacional de aplicación</span>
                  <span>
                    Defensa de las y los Consumidores. Para reclamos ingrese
                    <a
                      href="https://www.argentina.gob.ar/produccion/defensadelconsumidor/formulario"
                      target="_blank"
                      rel="noreferrer"
                      className="font-bold hover:underline"
                    >
                      {' '}
                      aquí.
                    </a>
                  </span>
                </div>
              </div>
            )}
            <LogosFooter />
          </div>
        </div>
      </div>
      <CookiesMessage />
    </footer>
  );
}
